import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Popover, PopoverArrow, PopoverBody,
  PopoverContent, PopoverTrigger, Portal, Select, Text, Tooltip, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiClock, FiPause, FiPlay, FiSquare,
} from 'react-icons/fi';
import useTimeClock, { parseUtcMs, TimeClockDay, TimeClockRow } from '../hooks/useTimeClock';
import useWorkOrders from '../hooks/useWorkOrders';
import useItems from '../hooks/useItems';
import useCurrentUser from '../hooks/useCurrentUser';
import { WorkOrderStatus } from '../models/workOrder';

// item 1337 (Chad) — the WORK ORDER timer. Start with a note; stopping opens the booking
// dialog: pick the service item + the work order and the rounded hours land as a normal WO
// line (performed-by = you, line date = the clock's day). ACTUAL time shows here as the
// discount-style math and NEVER prints anywhere. Stopped-but-unbooked clocks nag as an
// orange count; a clock left running auto-locks at the end of its start day (Pacific).
// Chad's rounding (server-authoritative; mirrored here for display): first hour is a
// minimum — >1 min bills 1 hr; from hour two on, ≥15 min into the next hour rounds up.
const roundedHoursOf = (startedAt: string, stoppedAt: string): number => {
  const minutes = Math.floor((new Date(stoppedAt).getTime() - new Date(startedAt).getTime()) / 60000);
  if (minutes < 1) return 0;
  const h = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return h === 0 ? 1 : h + (rem >= 15 ? 1 : 0);
};

export const fmtElapsed = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};

const LAST_ITEM_KEY = 'basix.timeclock.lastItemId';

const TimerChip: React.FC = () => {
  const { isOutsideSales, user } = useCurrentUser();
  const {
    mine, start, stop, pause, resume, book, discard, isBusy,
  } = useTimeClock();
  // item 1338 — the note is editable right in the booking dialog (it becomes Work Performed)
  const [bookNote, setBookNote] = useState('');
  // item 1339b — per-day billed-hours overrides ("in case we accidentally let the timer run")
  const [dayHours, setDayHours] = useState<Record<string, number>>({});
  const toast = useToast();

  // live elapsed tick while a segment runs (paused clocks are frozen)
  // item 1343c — the tick state IS the wall clock, so re-renders are guaranteed
  const [nowMs, setNowMs] = useState(() => Date.now());
  const running = mine?.running ?? null; // the ACTIVE clock — may be paused
  useEffect(() => {
    if (!running || running.paused) return undefined;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  // item 1338b — total elapsed = banked segments + the live one; paused time never counts
  const elapsedMs = (c: TimeClockRow): number => c.accumulatedSeconds * 1000
    + (c.segmentStartedAt ? Math.max(0, nowMs - parseUtcMs(c.segmentStartedAt)) : 0);

  // start popover
  const startPop = useDisclosure();
  const [note, setNote] = useState('');

  // booking dialog — works through the unbooked pile, oldest first
  const bookModal = useDisclosure();
  const [bookingId, setBookingId] = useState<string | null>(null);
  const unbooked = useMemo(() => mine?.unbooked ?? [], [mine]);
  const booking: TimeClockRow | undefined = useMemo(
    () => unbooked.find((c) => c.id === bookingId) ?? undefined,
    [unbooked, bookingId],
  );

  // pickers — open WORK ORDERS only (never sales orders), cascaded like the 1302 picker
  const { data: workOrders } = useWorkOrders();
  const { data: itemsData } = useItems();
  const openWorkOrders = useMemo(() => (workOrders?.index ?? [])
    .filter((w: any) => !w.isVoid && !w.isSalesOrder && w.status !== WorkOrderStatus.InvoicesGenerated)
    .sort((a: any, b: any) => (b.workOrderNumber ?? '').localeCompare(a.workOrderNumber ?? '')), [workOrders]);
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');
  const [itemId, setItemId] = useState<string>(() => {
    try { return window.localStorage.getItem(LAST_ITEM_KEY) ?? ''; } catch { return ''; }
  });
  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    openWorkOrders.forEach((w: any) => { if (w.customerId && !seen.has(w.customerId)) seen.set(w.customerId, w.customerName ?? 'Customer'); });
    return Array.from(seen.entries());
  }, [openWorkOrders]);
  const customerProjects = useMemo(() => {
    const seen = new Map<string, string>();
    openWorkOrders.filter((w: any) => w.customerId === customerId)
      .forEach((w: any) => { if (w.projectId && !seen.has(w.projectId)) seen.set(w.projectId, w.projectName ?? 'Project'); });
    return Array.from(seen.entries());
  }, [openWorkOrders, customerId]);
  const projectWorkOrders = useMemo(
    () => openWorkOrders.filter((w: any) => w.projectId === projectId),
    [openWorkOrders, projectId],
  );
  // item 1343 (Chad's rule, no override) — a PROPOSAL-BACKED WO is a bid job: time TRACKS
  // (job costing), it never bills. The dialog flips itself when such a WO is picked.
  const pickedWo = useMemo(
    () => openWorkOrders.find((w: any) => w.id === workOrderId),
    [openWorkOrders, workOrderId],
  );
  const isBid = !!(pickedWo as any)?.proposalId;
  const serviceItems = useMemo(() => ((itemsData?.index ?? []) as any[])
    .filter((i) => !i.isHardware && !i.isArchive && i.sku)
    .sort((a, b) => (a.sku ?? '').localeCompare(b.sku ?? '')), [itemsData]);

  const openBooking = (id: string) => {
    setBookingId(id);
    const row = (mine?.unbooked ?? []).find((c) => c.id === id);
    setBookNote(row?.note ?? ''); // item 1338
    setDayHours(Object.fromEntries((row?.days ?? []).map((d) => [d.date, d.hours]))); // item 1339b
    setCustomerId(''); setProjectId(''); setWorkOrderId('');
    bookModal.onOpen();
  };

  // item 1337b — the MY TIME page (Tools) drives this chip's dialogs by event
  useEffect(() => {
    const onBookEvt = (e: Event) => { const id = (e as CustomEvent).detail as string; if (id) openBooking(id); };
    const onStartEvt = () => startPop.onOpen();
    window.addEventListener('basix:timeclock-book', onBookEvt);
    window.addEventListener('basix:timeclock-start', onStartEvt);
    return () => {
      window.removeEventListener('basix:timeclock-book', onBookEvt);
      window.removeEventListener('basix:timeclock-start', onStartEvt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // item 1346b (Chad: "like Google Meet — it auto throws a window in the corner when you
  // click a different tab") — Document Picture-in-Picture: a real always-on-top OS mini
  // window with the ticking clock + Stop / Stop-&-book. Manual via the PiP buttons (a user
  // gesture is required by the browser); AUTO on tab-switch once the user grants the site
  // Chrome/Brave's "Automatically enter picture-in-picture" permission (we register the
  // mediaSession handler so the browser offers it). Plain DOM inside the PiP window — no
  // React portal needed for a clock and two buttons.
  const pipRef = React.useRef<Window | null>(null);
  const runningRef = React.useRef<TimeClockRow | null>(null);
  runningRef.current = running;
  const closePip = React.useCallback(() => {
    try { pipRef.current?.close(); } catch { /* gone */ }
    pipRef.current = null;
  }, []);
  const openPip = React.useCallback(async () => {
    const dpp = (window as any).documentPictureInPicture;
    if (!dpp || pipRef.current) return;
    try {
      const pip: Window = await dpp.requestWindow({ width: 340, height: 84 });
      pipRef.current = pip;
      const doc = pip.document;
      doc.body.style.cssText = 'margin:0;background:#171923;color:#fff;font-family:ui-monospace,Menlo,monospace;display:flex;align-items:center;gap:10px;padding:10px 12px;overflow:hidden;';
      const rec = doc.createElement('span');
      rec.textContent = 'REC';
      rec.style.cssText = 'background:#2f855a;color:#fff;font-size:10px;font-weight:bold;padding:2px 6px;border-radius:4px;letter-spacing:.08em;flex-shrink:0;';
      const clock = doc.createElement('span');
      clock.style.cssText = 'font-size:26px;font-weight:800;flex-shrink:0;';
      const noteEl = doc.createElement('span');
      noteEl.style.cssText = 'font-size:12px;opacity:.7;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;min-width:0;font-family:system-ui,sans-serif;';
      const mkBtn = (label: string, bg: string) => {
        const b = doc.createElement('button');
        b.textContent = label;
        b.style.cssText = `background:${bg};color:#fff;border:none;border-radius:6px;padding:6px 10px;font-size:12px;font-weight:600;cursor:pointer;flex-shrink:0;`;
        return b;
      };
      const stopBtn = mkBtn('⏸', '#4a5568');
      stopBtn.title = 'Stop — resume any time';
      const bookBtn = mkBtn('■', '#c53030');
      bookBtn.title = 'Stop & book';
      doc.body.append(rec, clock, noteEl, stopBtn, bookBtn);
      const tick = pip.setInterval(() => {
        const r = runningRef.current;
        if (!r) { closePip(); return; }
        clock.textContent = fmtElapsed(r.accumulatedSeconds * 1000
          + (r.segmentStartedAt ? Math.max(0, Date.now() - parseUtcMs(r.segmentStartedAt)) : 0));
        noteEl.textContent = r.note || '';
      }, 500);
      stopBtn.onclick = () => { const r = runningRef.current; if (r) pause(r.id); closePip(); };
      bookBtn.onclick = async () => {
        const r = runningRef.current;
        closePip();
        window.focus();
        if (r) { await stop(r.id); openBooking(r.id); }
      };
      pip.addEventListener('pagehide', () => { pip.clearInterval(tick); pipRef.current = null; });
    } catch { pipRef.current = null; /* denied/unsupported — the pill still exists */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closePip, pause, stop]);
  // item 1346d (Chad: "straight to the corner of the screen… auto show and hide based on
  // whether the tab is open — the weird undock in the webpage stinks") — the in-page pill is
  // GONE. The OS corner window is the only undock:
  //   • auto-HIDE: returning to the Basix tab always closes it (we own this half);
  //   • auto-SHOW on leaving the tab rides Chromium's auto-PiP (mediaSession handler below) —
  //     the browser only fires it once the user grants "Automatically enter picture-in-
  //     picture" for the site (no page may open windows uninvited; browser law);
  //   • the strip's pop-out button opens it manually any time (its click dispatches the
  //     event below synchronously, so the user gesture carries into requestWindow).
  useEffect(() => {
    if (!running) { closePip(); return undefined; }
    try {
      (navigator as any).mediaSession?.setActionHandler?.('enterpictureinpicture', () => openPip());
    } catch { /* older browser — the manual pop-out button still works */ }
    const onPipEvt = () => openPip();
    const onVis = () => { if (document.visibilityState === 'visible') closePip(); };
    window.addEventListener('basix:timeclock-pip', onPipEvt);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      try { (navigator as any).mediaSession?.setActionHandler?.('enterpictureinpicture', null); } catch { /* noop */ }
      window.removeEventListener('basix:timeclock-pip', onPipEvt);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [running, openPip, closePip]);

  if (isOutsideSales || !user) return null;

  const onStart = async () => {
    try {
      await start(note.trim());
      setNote('');
      startPop.onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Timer not started', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const onStop = async () => {
    if (!running) return;
    try {
      await stop(running.id);
      openBooking(running.id); // straight into booking — the whole point
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not stop the timer', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const onBook = async () => {
    if (!booking || !workOrderId || (!itemId && !isBid)) return;
    try {
      const res: any = await book(booking.id, workOrderId, isBid ? '00000000-0000-0000-0000-000000000000' : itemId, bookNote,
        isBid ? undefined : (booking.days ?? []).map((d) => ({ date: d.date, hours: dayHours[d.date] ?? d.hours }))); // item 1339b/1343
      try { window.localStorage.setItem(LAST_ITEM_KEY, itemId); } catch { /* best-effort */ }
      const d = res?.data;
      let desc = 'Added to the work order.';
      if (d?.tracked) desc = `Tracked on ${d.workOrderNumber} — bid job, nothing billed.`; // item 1343
      else if (d) desc = `${d.hours} hr${d.hours === 1 ? '' : 's'} added to ${d.workOrderNumber}.`;
      toast({ status: 'success', title: d?.tracked ? 'Time tracked' : 'Time booked', description: desc });
      const next = unbooked.find((c) => c.id !== booking.id);
      if (next) setBookingId(next.id); else bookModal.onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Not booked', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const onDiscard = async () => {
    if (!booking) return;
    try {
      await discard(booking.id);
      const next = unbooked.find((c) => c.id !== booking.id);
      if (next) setBookingId(next.id); else bookModal.onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Not discarded', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const rounded = (booking?.days ?? []).reduce((s, d) => s + d.hours, 0); // item 1339 — per-day sum

  return (
    <>
      {running ? (
        <HStack spacing={1}>
          {/* item 1339 — the chip: elapsed stops (idle, resumable); the square stops & books */}
          <Tooltip label={running.note || 'Running — stop (resume any time)'} hasArrow>
            <Button
              size="sm"
              variant="outline"
              colorScheme="green"
              leftIcon={<FiPause />}
              onClick={() => pause(running.id)}
              isLoading={isBusy}
              fontFamily="mono"
            >
              {fmtElapsed(elapsedMs(running))}
            </Button>
          </Tooltip>
          <Tooltip label="Stop & book" hasArrow>
            <IconButton
              aria-label="Stop & book"
              size="sm"
              variant="outline"
              colorScheme="red"
              icon={<FiSquare />}
              onClick={onStop}
              isLoading={isBusy}
            />
          </Tooltip>
        </HStack>
      ) : (
        <Popover isOpen={startPop.isOpen} onClose={startPop.onClose} placement="bottom-end">
          <PopoverTrigger>
            <Box position="relative">
              <Tooltip label="Start a work-order timer" hasArrow>
                <IconButton
                  aria-label="Start timer"
                  icon={unbooked.length > 0 ? <FiClock /> : <FiPlay />}
                  variant="ghost"
                  onClick={() => startPop.onToggle()} // item 1338 — unbooked time never blocks a new timer
                />
              </Tooltip>
              {/* item 1337 — the NAG: stopped time not yet booked to a work order */}
              {unbooked.length > 0 && (
                <Badge
                  position="absolute"
                  top="-1px"
                  right="-1px"
                  borderRadius="full"
                  colorScheme="orange"
                  fontSize="0.6rem"
                  cursor="pointer"
                  onClick={() => openBooking(unbooked[0].id)}
                >
                  {unbooked.length}
                </Badge>
              )}
            </Box>
          </PopoverTrigger>
          {/* item 1337c — PORTAL: without it the popover renders inside the topbar's stacking
              context and hides UNDER a layer-2 panel (Chad: "does nothing") */}
          <Portal>
            <PopoverContent w="280px">
              <PopoverArrow />
              <PopoverBody>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>Start a timer</Text>
                <Input
                  size="sm"
                  placeholder="What are you working on?"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onStart(); }}
                />
                <Button mt={2} size="sm" w="full" leftIcon={<FiPlay />} onClick={onStart} isLoading={isBusy}>
                  Start
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}

      <Modal isOpen={bookModal.isOpen} onClose={bookModal.onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Book time to a work order</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {booking && (
              <>
                {booking.note && <Text fontSize="sm" mb={1}><b>{booking.note}</b></Text>}
                {/* item 1343 — bid job: tracked, not billed; actuals shown read-only */}
                {isBid && (
                  <Box mb={3} px={3} py={2} borderWidth="1px" borderRadius="md" borderColor="purple.400">
                    <Text fontSize="sm" fontWeight="semibold" color="purple.300">
                      {`${(pickedWo as any)?.proposalNumber ? `Tied to ${(pickedWo as any).proposalNumber} — ` : ''}bid job: this time will be TRACKED, not billed.`}
                    </Text>
                    <Text fontSize="xs" opacity={0.7} mt={1}>
                      Actual hours land on the work order&apos;s internal Tracked time (job costing) —
                      no lines, no totals, nothing prints. Billable extras belong on a standalone work order.
                    </Text>
                    {(booking.days ?? []).map((d: TimeClockDay) => (
                      <HStack key={d.date} spacing={3} mt={1}>
                        <Text fontSize="xs" w="90px" flexShrink={0}>{new Date(`${d.date}T12:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' })}</Text>
                        <Text fontSize="xs" fontFamily="mono" opacity={0.8}>
                          {`${Math.floor(d.seconds / 3600)}h ${String(Math.floor((d.seconds % 3600) / 60)).padStart(2, '0')}m actual`}
                        </Text>
                      </HStack>
                    ))}
                  </Box>
                )}
                {/* item 1339 — the per-day split: one WO line per day, hours editable (1339b).
                    The ACTUAL time stays on the clock and never prints anywhere. */}
                {!isBid && (booking.days ?? []).map((d: TimeClockDay) => (
                  <HStack key={d.date} spacing={3} mb={1.5}>
                    <Text fontSize="sm" w="90px" flexShrink={0}>{new Date(`${d.date}T12:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' })}</Text>
                    <Text fontSize="xs" color="gray.500" w="90px" flexShrink={0} fontFamily="mono">
                      {`${Math.floor(d.seconds / 3600)}h ${String(Math.floor((d.seconds % 3600) / 60)).padStart(2, '0')}m`}
                    </Text>
                    <Input
                      size="xs"
                      w="70px"
                      type="number"
                      min={0}
                      value={dayHours[d.date] ?? d.hours}
                      onChange={(e) => setDayHours((m) => ({ ...m, [d.date]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
                    />
                    <Text fontSize="xs" color="gray.500">hrs billed (0 = skip this day)</Text>
                  </HStack>
                ))}
                {!isBid && rounded < 1 && (
                  <Text fontSize="sm" color="orange.400" my={2}>Under a minute — nothing to bill. Discard it.</Text>
                )}
                <Box mb={2} />
                {/* item 1338 — the note becomes the WO's Work Performed entry (and rides to the invoice) */}
                <Input
                  size="sm"
                  mb={2}
                  placeholder="What was done? (lands in Work Performed)"
                  value={bookNote}
                  onChange={(e) => setBookNote(e.target.value)}
                />
                {!isBid && (
                <Select size="sm" mb={2} value={itemId} onChange={(e) => setItemId(e.target.value)}>
                  <option value="">Service item…</option>
                  {serviceItems.map((i: any) => (
                    <option key={i.id} value={i.id}>{`${i.sku}${i.description ? ` — ${String(i.description).slice(0, 60)}` : ''}`}</option>
                  ))}
                </Select>
                )}
                <Select size="sm" mb={2} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setProjectId(''); setWorkOrderId(''); }}>
                  <option value="">Customer…</option>
                  {customers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </Select>
                <Select size="sm" mb={2} value={projectId} isDisabled={!customerId} onChange={(e) => { const pid = e.target.value; setProjectId(pid); const only = openWorkOrders.filter((w: any) => w.projectId === pid); setWorkOrderId(only.length === 1 ? only[0].id : ''); }}>
                  <option value="">Project…</option>
                  {customerProjects.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </Select>
                <Select size="sm" value={workOrderId} isDisabled={!projectId} onChange={(e) => setWorkOrderId(e.target.value)}>
                  <option value="">Work order…</option>
                  {projectWorkOrders.map((w: any) => (
                    <option key={w.id} value={w.id}>{`${w.workOrderNumber ?? ''} — ${w.workOrderTitle ?? ''}`}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={3}>
                  Books a normal line on the work order: the item, qty = the billed hours, dated to the
                  clock&apos;s day, performed by you. Once booked, this clock is locked.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {/* item 1338b (Chad: "if stop, allow pause instead of later") — Pause un-stops the
                clock so tracking can continue; closing with X still just leaves it unbooked. */}
            <Button
              variant="ghost"
              leftIcon={<FiPause />}
              mr={3}
              isLoading={isBusy}
              onClick={async () => {
                if (!booking) { bookModal.onClose(); return; }
                try {
                  await pause(booking.id);
                  bookModal.onClose();
                  toast({ status: 'info', title: 'Paused', description: 'Still on the clock — resume from the timer chip when you pick it back up.' });
                } catch (e: any) {
                  toast({ status: 'error', title: 'Not paused', description: String(e?.response?.data ?? '') });
                }
              }}
            >
              Pause
            </Button>
            <Button variant="outline" colorScheme="red" mr={3} onClick={onDiscard} isLoading={isBusy}>Discard</Button>
            <Button
              onClick={onBook}
              isDisabled={!workOrderId || (!isBid && (!itemId
                || (booking?.days ?? []).reduce((s, d) => s + (dayHours[d.date] ?? d.hours), 0) < 1))}
              isLoading={isBusy}
            >
              {isBid ? 'Track time' : 'Book time'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TimerChip;
